import axios from 'axios';
import { BehaviorSubject, Observable, of, Subject, timer } from 'rxjs';
import { fromPromise } from 'rxjs/internal/observable/fromPromise';
import { exhaustMap, filter, map, switchMap } from 'rxjs/internal/operators';
import { IPrediction } from '../../interfaces/stadtbus/predictions'
import { lazyInject, provideSingleton } from '../../inversify';
import LocalStorageService from '../local_storage_service';
import Configuration from './../configuration';

interface ITripsJourneyStop {
  // only one of the two can be undefined.
  arrivalTimestamp?: Date,
  departureTimestamp?: Date,
  stopId: string,
  sequenceNumber: number,
}

interface ITripsJourney {
  journeyRef: string,
  operationDate: string
  direction: string,
  destinationText: string,
  originStopPoint: string,
  destinationStopPoint: string,
  startTimestamp: Date,
  endTimestamp: Date,
  line: string,
  stops: ITripsJourneyStop[],
}

interface ITripsResponse {
  [key: string]: ITripsJourney[];
}

function parseITripsResponse(jsonString: string | any): ITripsResponse {
  const response = typeof jsonString === 'object' ? jsonString as ITripsResponse : JSON.parse(jsonString) as ITripsResponse;

  Object.keys(response).forEach(k => {
    response[k].forEach(j => {
      j.startTimestamp = new Date(j.startTimestamp);
      j.endTimestamp = new Date(j.endTimestamp);

      j.stops.forEach(s => {
        s.arrivalTimestamp = s.arrivalTimestamp ? new Date(s.arrivalTimestamp) : undefined;
        s.departureTimestamp = s.departureTimestamp ? new Date(s.departureTimestamp) : undefined;
      })
    });
  });

  return response;
}

@provideSingleton(TimetableService)
class TimetableService {
  @lazyInject(Configuration)
  private configuration: Configuration;
  @lazyInject(LocalStorageService)
  private localStorageService: LocalStorageService;

  private onTrips: BehaviorSubject<ITripsResponse | undefined> = new BehaviorSubject<ITripsResponse | undefined>(undefined);

  private readonly onUpdateTrips: Subject<void> = new Subject<void>();

  constructor() {
    // Subscribe to the stored trips from the local storage. Writes to the localStorageService update the trips.
    this.localStorageService.getItem('journeys', undefined, parseITripsResponse)
      .pipe(
        filter(journeys => {
          if (journeys && typeof journeys === 'object' && !Array.isArray(journeys)) {
            return true;
          }

          this.onUpdateTrips.next();
          return false;
        }),
      )
      .subscribe(this.onTrips);

    this.onUpdateTrips.pipe(
      exhaustMap(() =>
        fromPromise(axios
          .get(`${this.configuration.apiBaseUrl()}/timetable/trips`)
          .then(response => response.data as any)
          .catch(() => undefined)
        )
      ),
    ).subscribe(t =>
      t !== undefined && this.localStorageService.setItem('journeys', parseITripsResponse(t)));

    // Start the update process to receive new trips each 2 minutes.
    timer(0, 120000).subscribe(() => this.onUpdateTrips.next());
  }

  public tripToPrediction(journey: ITripsJourney, stop: ITripsJourneyStop) {
    return {
      delay: 0,
      estimatedTime: new Date(stop.departureTimestamp! || stop.arrivalTimestamp!),
      isRealTime: false,
      journeyRef: journey.journeyRef,
      lineDestination: journey.destinationText,
      lineName: journey.line,
      operationDate: journey.operationDate,
      shouldTime: new Date(stop.departureTimestamp! || stop.arrivalTimestamp!),
      stopID: stop.stopId,
      visitNumber: stop.sequenceNumber,
      visited: false,
    } as IPrediction;
  }

  public onTripTimetable(journeyRef: string, operationDate: string): Observable<IPrediction[]> {
    return this.onTrips.asObservable().pipe(
      // undefined => no data received yet.
      filter(t => t !== undefined),

      map((tripsResponse: ITripsResponse) => {
        const journey = Object.keys(tripsResponse).find(key => journeyRef.startsWith(key));

        if (journey !== undefined) {
          const trips = tripsResponse[journey];
          return trips.find(t => t.journeyRef === journeyRef && t.operationDate === operationDate);
        }

        return undefined;
      }),

      map(trip => trip === undefined ? [] : trip.stops.map(entry => this.tripToPrediction(trip, entry))),
    );
  }

  public onStopTimetable(stopId: string, onlyToday: boolean = true): Observable<IPrediction[]> {
    return this.onTrips.asObservable().pipe(
      // undefined => no data received yet.
      filter(t => t !== undefined),

      // get all trips that contain the a stop at the [stopId] and don't end at the stop.
      map((t: ITripsResponse) => Object.keys(t).reduce((acc0, key) => t[key].reduce((acc1, trip) => {
        const stop = trip.stops.find(s => s.stopId === stopId && s.departureTimestamp !== undefined);
        if (stop !== undefined) {
          acc1.push(trip);
        }
        return acc1;
      }, acc0), [] as ITripsJourney[])),

      // convert the journeys to [IPrediction]s.
      map(trips => trips.map(t => {
        const stopInfo = t.stops.find(s => s.stopId === stopId)!;

        return this.tripToPrediction(t, stopInfo);
      })),

      // sort by ascending should time.
      map(trips => trips.sort((a, b) => a.shouldTime.getTime() - b.shouldTime.getTime())),

      // Filter the predictions if only the current day should be displayed. This is updated each 60 seconds.
      switchMap(predictions => {
        if (onlyToday) {
          return timer(0, 60000)
            .pipe(map(() => {
              const today = new Date();
              return predictions.filter(p =>
                p.shouldTime.getDate() === today.getDate()
                && p.shouldTime.getMonth() === today.getMonth()
                && p.shouldTime.getFullYear() === today.getFullYear());
            }))
        } else {
          return of(predictions);
        }
      }),
    );
  }
}

export default TimetableService;



// WEBPACK FOOTER //
// ./src/api/stadtbus/timetable_service.ts